import { useState } from "react";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import { useTranslation } from "react-i18next";
import { scrollToSection } from "@/lib/scroll";

const tiers = [
  {
    key: "starter",
    name: "Starter",
    monthly: "$24.99",
    annual: "$4.99",
    credits: "100 credits",
    bonus: null,
    features: ["Build any app", "In-app code edits", "Publish to App Store", "Buy additional credits", "Niche Finder", "Winning ads library"],
    popular: false,
  },
  {
    key: "builder",
    name: "Builder",
    monthly: "$34.99",
    annual: "$6.99",
    credits: "200 credits",
    bonus: "+ 200 bonus credits for the first month",
    features: ["Build any app", "Publish to the App Store for free", "Workspace-private projects", "In-app code edits", "Backend functions", "Buy additional credits", "Email support", "Niche Finder", "Winning ads library"],
    popular: true,
  },
  {
    key: "studio",
    name: "Studio",
    monthly: "$99.99",
    annual: "$19.99",
    credits: "1,000 credits",
    bonus: "+ 1,000 bonus credits for the first month",
    features: ["Build any app", "Faster app generation", "Full project memory", "Backend functions", "RevenueCat integration", "Supabase integration", "Track revenue & downloads", "Winning ads library", "App Store & Play rankings", "Premium support"],
    popular: false,
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(true);
  const { t } = useTranslation();
  const go = () => scrollToSection("#waitlist");

  return (
    <section id="pricing" className="py-28 md:py-36 border-t border-white/10 relative overflow-hidden">
      <div className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-0 w-[45rem] h-[30rem] bg-[#0055FF]/8 blur-[130px] rounded-full" />
      <div className="max-w-6xl mx-auto px-6 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="text-center mb-10"
        >
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-[#00F0FF] mb-4">{t("pricing.kicker")}</p>
          <h2 className="font-display font-bold text-4xl md:text-5xl tracking-tight">{t("pricing.title")}</h2>
          <p className="text-slate-400 mt-4 max-w-2xl mx-auto">{t("pricing.subtitle")}</p>
        </motion.div>

        <div className="flex items-center justify-center gap-3 mb-14">
          <button
            onClick={() => setAnnual(false)}
            className={`font-mono text-xs uppercase tracking-widest px-4 py-2 rounded-full transition-colors duration-300 ${!annual ? "bg-white/10 text-white" : "text-slate-500"}`}
            data-testid="pricing-monthly"
          >
            {t("pricing.monthly")}
          </button>
          <button
            onClick={() => setAnnual(true)}
            className={`font-mono text-xs uppercase tracking-widest px-4 py-2 rounded-full transition-colors duration-300 inline-flex items-center gap-2 ${annual ? "bg-white/10 text-white" : "text-slate-500"}`}
            data-testid="pricing-annual"
          >
            {t("pricing.annual")} <span className="bg-[#00F0FF] text-black text-[10px] font-bold px-2 py-0.5 rounded-full">{t("pricing.save")}</span>
          </button>
        </div>

        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {tiers.map((t2, i) => (
            <motion.div
              key={t2.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, delay: i * 0.08 }}
              className={`relative rounded-3xl ${t2.popular ? "tracing-beam" : "border border-white/10 bg-[#0a0a0a]"}`}
              data-testid={`pricing-${t2.name.toLowerCase()}`}
            >
              <div className={`relative rounded-3xl p-8 h-full flex flex-col ${t2.popular ? "bg-[#0a0a0a]" : ""}`}>
                {t.popular && (
                  <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#00F0FF] text-black text-[10px] font-bold font-mono uppercase tracking-widest px-3 py-1 rounded-full">
                    {t("pricing.mostPopular")}
                  </span>
                )}
                <h3 className="font-display font-bold text-2xl">{t(`pricing.tiers.${t2.key}.name`)}</h3>
                <p className="text-slate-500 text-sm mt-1 h-10">{t(`pricing.tiers.${t2.key}.blurb`)}</p>

                <div className="mt-5 flex items-end gap-2">
                  {annual && <span className="text-slate-600 line-through text-lg mb-1">{t2.monthly}</span>}
                  <span className="font-display font-extrabold text-5xl">{annual ? t2.annual : t2.monthly}</span>
                  <span className="text-slate-500 mb-2">{t("pricing.perMonthShort")}</span>
                </div>
                <p className="font-mono text-[11px] text-slate-500 mt-1">{annual ? t("pricing.billedAnnually") : t("pricing.billedMonthly")}</p>

                <div className="mt-5 rounded-2xl bg-[#0055FF]/10 border border-[#0055FF]/25 px-4 py-3">
                  <p className="font-display font-bold text-white">{t2.credits} <span className="text-slate-400 font-body font-normal text-sm">{t("pricing.perMonth")}</span></p>
                  {t2.bonus && <p className="text-[#00F0FF] text-xs mt-1">{t2.bonus}</p>}
                </div>

                <button
                  onClick={go}
                  className={`mt-6 w-full py-3.5 rounded-2xl font-semibold transition-[transform,background-color,box-shadow] duration-300 hover:-translate-y-0.5 ${
                    t2.popular
                      ? "bg-[#0055FF] text-white shadow-[0_0_40px_rgba(0,85,255,0.5)] hover:bg-[#0047d6]"
                      : "bg-white text-black hover:bg-[#00F0FF]"
                  }`}
                  data-testid={`pricing-cta-${i}`}
                >
                  {t("pricing.getStarted")}
                </button>

                <ul className="mt-7 space-y-3">
                  {t2.features.map((f) => (
                    <li key={f} className="flex items-start gap-3 text-slate-300 text-sm">
                      <span className="w-5 h-5 rounded-full bg-[#0055FF]/20 flex items-center justify-center shrink-0 mt-0.5">
                        <Check size={11} className="text-[#00F0FF]" />
                      </span>
                      {f}
                    </li>
                  ))}
                </ul>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
